import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMasterPartnerAuth } from '../contexts/MasterPartnerAuthContext';
import { 
  Home, Users, Settings, LogOut, Menu, IndianRupee, RefreshCw, 
  Loader2, Clock, CheckCircle, CreditCard, Eye, Filter, X, Building2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MasterPartnerCommissions = () => {
  const navigate = useNavigate();
  const { masterPartner, logout, getAuthHeader } = useMasterPartnerAuth();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState('');

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const [commissionsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/master-partner/portal/commissions?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API}/master-partner/portal/commissions/summary`, { headers: getAuthHeader() })
      ]);
      
      setCommissions(commissionsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/master-partner/login');
  };

  const viewPaymentScreenshot = (screenshotPath) => {
    setScreenshotUrl(`${BACKEND_URL}${screenshotPath}`);
    setShowScreenshotModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      case 'approved': return <CreditCard className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b">
          <h1 className="text-xl font-bold text-gray-900">Master Partner</h1>
          <p className="text-sm text-gray-500 truncate">{masterPartner?.company_name}</p>
        </div>
        
        <nav className="p-4 space-y-2">
          <Link 
            to="/master-partner/dashboard" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Home className="w-5 h-5" />
            Dashboard
          </Link>
          <Link 
            to="/master-partner/partners" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Users className="w-5 h-5" />
            My Partners
          </Link>
          <Link 
            to="/master-partner/commissions" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-purple-50 text-purple-700 font-medium"
          >
            <IndianRupee className="w-5 h-5" />
            Commissions
          </Link>
          <Link 
            to="/master-partner/profile" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Settings className="w-5 h-5" />
            Profile
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">My Commissions</h2>
            <button
              onClick={fetchData}
              className="p-2 rounded-lg hover:bg-gray-100"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          {/* Commission Summary */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="text-amber-600" size={20} />
                  <span className="text-sm text-amber-700">Pending Approval</span>
                </div>
                <p className="text-2xl font-bold text-amber-700">
                  ₹{(summary.pending?.amount || 0).toLocaleString()}
                </p>
                <p className="text-xs text-amber-600">{summary.pending?.count || 0} records</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="text-blue-600" size={20} />
                  <span className="text-sm text-blue-700">Ready for Payout</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">
                  ₹{(summary.approved?.amount || 0).toLocaleString()}
                </p>
                <p className="text-xs text-blue-600">{summary.approved?.count || 0} records</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="text-sm text-green-700">Paid Out</span>
                </div>
                <p className="text-2xl font-bold text-green-700">
                  ₹{(summary.paid?.amount || 0).toLocaleString()}
                </p>
                <p className="text-xs text-green-600">{summary.paid?.count || 0} records</p>
              </div>
            </div>
          )}

          {/* Filter */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </div>

          {/* Commission Records */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-900">Commission History</h3>
            </div>
            
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
              </div>
            ) : commissions.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <IndianRupee className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium">No Commissions Yet</h3>
                <p className="mt-1">
                  {statusFilter 
                    ? 'No commissions match this filter' 
                    : 'Commissions will appear here when your partners generate leads'}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {commissions.map((comm) => (
                  <div key={comm.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="w-4 h-4 text-purple-500" />
                          <span className="font-medium text-gray-900">{comm.partner_name}</span>
                        </div>
                        <p className="text-sm text-gray-600">{comm.lead_name}</p>
                        <p className="text-sm text-gray-500">{comm.service_type}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Invoice: {comm.invoice_number}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">₹{comm.commission_amount?.toLocaleString()}</p>
                        <span className={`inline-flex items-center gap-1 mt-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(comm.status)}`}>
                          {getStatusIcon(comm.status)}
                          {comm.status}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        {comm.source_description}
                      </p>
                      <div className="flex items-center gap-3">
                        {comm.status === 'paid' && comm.payment_screenshot && (
                          <button
                            onClick={() => viewPaymentScreenshot(comm.payment_screenshot)}
                            className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                          >
                            <Eye size={12} />
                            Payment Proof
                          </button>
                        )}
                        <span className="text-xs text-gray-400">
                          {new Date(comm.created_at).toLocaleDateString('en-IN')}
                        </span>
                      </div>
                    </div>
                    
                    {comm.status === 'paid' && (
                      <div className="mt-2 text-xs text-green-600">
                        Paid on {new Date(comm.paid_at).toLocaleDateString('en-IN')}
                        {comm.payment_reference && ` • Ref: ${comm.payment_reference}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Payment Screenshot Modal */}
      {showScreenshotModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Payment Proof</h3>
              <button
                onClick={() => setShowScreenshotModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-auto">
              <img 
                src={screenshotUrl} 
                alt="Payment Screenshot"
                className="max-w-full rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterPartnerCommissions;
